import { useMemo, useState } from 'react';
import { Pencil, X, Smartphone, Unplug } from 'lucide-react';
import { useRosterClasse } from '../../hooks/fetch_api_hook/useRosterClasse';
import { useGroupe } from '../../hooks/fetch_api_hook/useGroupe';
import { useAssignationsCarte } from '../../hooks/fetch_api_hook/useAssignationsCarte';
import { useCartesAruco } from '../../hooks/fetch_api_hook/useCartesAruco';
import { usePostApi } from '../../hooks/post_api_hook/usePostApi';
import useDeleteApi from '../../hooks/delete_api_hook/useDeleteApi';
import { QCM_NAVY_BG, FONT_TITRE, QCM_GREEN, QCM_AMBER } from '../../utils/qcmTheme';
import { url } from '../../utils/confserveur';
import QrScan from './QrScan';

/**
 * L'écran projeté d'AVANT-lancement, le seul (chantier jeton, 2026-09-04) :
 * la DISTRIBUTION des cartes et le rattachement du téléphone, sur le même
 * écran, en même temps.
 *
 *   - à gauche, en grand : la liste « carte 3 : Alice ». Le paquet de cartes
 *     est au professeur, sans nom dessus ; chaque élève lit son numéro ici et
 *     prend sa carte. Les numéros ne bougent jamais dans une classe : au bout
 *     de trois séances, la liste n'est plus qu'un filet ;
 *   - à droite : le QR à scanner, tant qu'aucun appareil n'est relié ; puis
 *     « Appareil connecté · Détacher ». La liste reste jusqu'au lancement.
 *
 * Le professeur corrige une carte depuis CE poste, et seulement ici : une
 * fois le quizz lancé, réattribuer réécrirait l'origine de réponses déjà
 * enregistrées. Aucune commande de pilotage : le quizz se lance depuis le
 * téléphone.
 *
 * Séance de GROUPE (contrat back du 2026-09-21) : la correspondance carte →
 * élève est `overrides_carte` de la séance, remplie dès la création (carte 0
 * au premier de l'alphabet, 30 au plus), et les noms viennent du groupe. La
 * liste s'affiche donc avant le lancement comme pour une classe, en lecture
 * seule : elle ne se corrige pas d'ici. Une séance d'avant ce régime a un
 * `overrides_carte` vide, calculé au lancement : on le dit.
 *
 * Le jeu de cartes, lui, est le même pour tout le monde : 30 cartes par
 * professeur, sans nom, imprimées une fois (bouton « Imprimer les 30 cartes »
 * dans la liste des évaluations). Ce qui change d'une séance à l'autre, c'est
 * cette liste, lue à l'écran le jour J.
 */

// Les listes de l'API sortent nues (aucune pagination DRF n'est configurée),
// mais les appelants historiques se protégeaient déjà d'un `{results: [...]}`.
const liste = (brut) => (Array.isArray(brut) ? brut : brut?.results || []);

const CADRE_LIGNE = {
  background: 'rgba(255,255,255,.06)',
  border: '1px solid rgba(255,255,255,.12)',
};

/** Le panneau du téléphone : QR ou « appareil connecté ». Exporté : la
 *  projection le réaffiche pendant le quizz quand le téléphone est perdu. */
export const PanneauAppareil = ({ sessionId, jeton, relie, onDetacher, grand = false, erreurJeton = null }) => {
  if (relie) {
    return (
      <div className="flex flex-col items-center gap-3" data-testid="appareil-connecte">
        <p
          className="flex items-center gap-2 font-black leading-none"
          style={{ color: QCM_GREEN, fontSize: grand ? 'clamp(28px, 4vw, 56px)' : 'clamp(18px, 1.8vw, 26px)', fontFamily: FONT_TITRE }}
        >
          <Smartphone size={grand ? 40 : 22} /> Appareil connecté
        </p>
        <p className="font-semibold text-white/60" style={{ fontSize: grand ? 'clamp(16px, 1.9vw, 26px)' : 'clamp(13px, 1.2vw, 16px)' }}>
          Lancez le quizz depuis votre appareil.
        </p>
        <button
          type="button"
          onClick={onDetacher}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold text-white/60 hover:text-white hover:bg-white/10 transition"
          style={{ border: '1px solid rgba(255,255,255,.25)' }}
        >
          <Unplug size={14} /> Détacher
        </button>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3">
      <p
        className="font-black leading-tight text-center"
        style={{ fontSize: grand ? 'clamp(24px, 3.2vw, 44px)' : 'clamp(15px, 1.4vw, 20px)', fontFamily: FONT_TITRE }}
      >
        Scannez avec votre téléphone
      </p>
      {jeton ? (
        <QrScan sessionId={sessionId} jeton={jeton} taille={grand ? 320 : 220} />
      ) : erreurJeton ? (
        <p className="text-red-300 text-sm" data-testid="qr-refus">Le serveur ne donne pas de QR ({erreurJeton}). Nouvel essai dans 90 s.</p>
      ) : (
        <p className="text-white/50 text-sm" data-testid="qr-attente">Préparation du QR…</p>
      )}
    </div>
  );
};

/** Le NUMÉRO est ce que l'élève cherche : sans « # » (bruit) et plus gros
 *  que le nom (revue prof 2026-09-01). */
const NumeroCarte = ({ code }) => (
  <span
    className="px-3 py-0.5 rounded-lg font-black tabular-nums"
    style={{ background: 'rgba(34,181,115,.18)', color: QCM_GREEN, fontSize: 'clamp(20px, 2.2vw, 32px)' }}
  >
    {code}
  </span>
);

const SansCarte = () => (
  <span
    className="px-3 py-1 rounded-lg font-bold"
    style={{ background: 'rgba(245,184,61,.16)', color: QCM_AMBER, fontSize: 'clamp(12px, 1.2vw, 17px)' }}
  >
    sans carte
  </span>
);

const ProjectionDistribution = ({ titre, classeId, groupeId = null, overrides = null, sessionId, jeton, relie, onDetacher, erreurJeton = null }) => {
  // Seules les ASSIGNATIONS changent sous nos écritures. La liste nominative de
  // la classe et le stock de cartes du professeur, eux, sont fixes le temps
  // d'une séance : les relire à chaque correction ferait repayer la requête la
  // plus chère des trois pour un résultat identique.
  const [refreshAssignations, setRefreshAssignations] = useState(0);
  const [eleveEnEdition, setEleveEnEdition] = useState(null);
  const [enCours, setEnCours] = useState(false);

  const avecClasse = Boolean(classeId);
  const avecGroupe = !avecClasse && Boolean(groupeId);
  const { data: classe, isLoading: rosterEnCharge } = useRosterClasse(classeId, 0, avecClasse ? undefined : 'not-enabled');
  const { data: groupe, isLoading: groupeEnCharge } = useGroupe(groupeId, avecGroupe ? undefined : 'not-enabled', 0);
  const { data: brutAssignations } = useAssignationsCarte(classeId, refreshAssignations, avecClasse ? undefined : 'not-enabled');
  const { data: brutCartes } = useCartesAruco(0);

  const [{ isError: postEnErreur }, doPost] = usePostApi(null, null);
  const { deleteData, isError: deleteEnErreur } = useDeleteApi();

  const eleves = useMemo(() => liste(classe?.eleves), [classe]);
  const assignations = useMemo(() => liste(brutAssignations), [brutAssignations]);
  const cartes = useMemo(() => liste(brutCartes), [brutCartes]);

  // Carte portée par chaque élève. Le serveur garantit « un élève, une carte
  // par prof » (lot cartes du 2026-09-05) : poser une carte remplace celle
  // qu'il avait, dans cette classe ou une autre. La liste reçue n'en a donc
  // qu'une par élève ; on garde la première par prudence.
  const carteDeLEleve = useMemo(() => {
    const map = new Map();
    assignations.forEach((a) => {
      if (!map.has(a.eleve)) map.set(a.eleve, a);
    });
    return map;
  }, [assignations]);

  // Une carte déjà prise DANS CETTE CLASSE n'est pas proposée. Le filtre
  // s'arrête à la classe : la même carte physique sert légitimement une autre
  // classe, c'est tout l'objet de `AssignationCarte`.
  const cartesLibres = useMemo(() => {
    const prises = new Set(assignations.map((a) => a.carte));
    return cartes.filter((c) => !prises.has(c.id));
  }, [cartes, assignations]);

  const sansCarte = useMemo(
    () => eleves.filter((e) => !carteDeLEleve.has(e.id)).length,
    [eleves, carteDeLEleve],
  );

  // Séance de groupe : les lignes viennent d'`overrides_carte` (code → id
  // d'élève), dans l'ordre des numéros, puis les élèves du groupe qui n'y
  // figurent pas, sans carte. Les noms viennent du groupe.
  const lignesGroupe = useMemo(() => {
    if (!avecGroupe) return [];
    const membres = liste(groupe?.eleves);
    const parId = new Map(membres.map((e) => [Number(e.id), e]));
    const codes = Object.entries(overrides || {})
      .map(([code, eleveId]) => ({ code: Number(code), eleve: parId.get(Number(eleveId)) }))
      .filter((l) => l.eleve && Number.isFinite(l.code))
      .sort((a, b) => a.code - b.code);
    const places = new Set(codes.map((l) => l.eleve.id));
    const sans = membres.filter((e) => !places.has(e.id)).map((e) => ({ code: null, eleve: e }));
    return [...codes, ...sans];
  }, [avecGroupe, groupe, overrides]);
  const groupeSansCorrespondance = avecGroupe && !groupeEnCharge && lignesGroupe.length > 0 && lignesGroupe.every((l) => l.code === null);

  const changerCarte = async (eleve, carteId) => {
    if (!classeId) return;
    const actuelle = carteDeLEleve.get(eleve.id);
    setEnCours(true);
    // Une requête par geste. Poser une carte : la création, le serveur
    // remplace lui-même la carte que l'élève portait. Retirer la carte :
    // la suppression seule.
    if (carteId) {
      await doPost(
        { carte: carteId, eleve: eleve.id, classe: classeId },
        `${url}classes/${classeId}/assignations/`,
      );
    } else if (actuelle) {
      await deleteData(`${url}classes/${classeId}/assignations/${actuelle.id}/`);
    }
    setEnCours(false);
    setEleveEnEdition(null);
    // La liste rechargée fait foi : `deleteData` avale ses erreurs, et une
    // écriture refusée doit se voir dans la liste plutôt que dans un état
    // local qui affirmerait le contraire.
    setRefreshAssignations((k) => k + 1);
  };

  const choisir = (eleve) => (evenement) => {
    const choix = evenement.target.value;
    if (!choix) return;
    changerCarte(eleve, choix === 'retirer' ? null : Number(choix));
  };

  const panneau = <PanneauAppareil sessionId={sessionId} jeton={jeton} relie={relie} onDetacher={onDetacher} erreurJeton={erreurJeton} grand={!avecClasse} />;

  // Séance de groupe : la liste en lecture seule, depuis la correspondance de la séance.
  if (avecGroupe) {
    const nbSansCarte = lignesGroupe.filter((l) => l.code === null).length;
    return (
      <div className="h-screen w-full flex flex-col overflow-hidden text-white px-8 pt-16 pb-6" style={{ background: QCM_NAVY_BG }}>
        <div className="proj-fade-up flex items-start gap-8 shrink-0">
          <div className="flex-1 min-w-0 space-y-1">
            {titre ? (
              <p className="font-bold text-[13px] uppercase text-white/40" style={{ letterSpacing: '.16em', fontFamily: FONT_TITRE }}>
                {titre}
              </p>
            ) : null}
            <p className="font-black leading-[1.15]" style={{ fontSize: 'clamp(24px, 3.2vw, 44px)', fontFamily: FONT_TITRE }}>
              Retrouve ton numéro de carte
            </p>
            <p className="font-semibold text-white/70 leading-snug" style={{ fontSize: 'clamp(15px, 1.6vw, 22px)' }}>
              Prends la carte qui porte ce numéro, puis attends le lancement.
            </p>
            {groupeSansCorrespondance ? (
              <p className="font-bold" style={{ color: QCM_AMBER, fontSize: 'clamp(13px, 1.3vw, 17px)' }} data-testid="groupe-sans-correspondance">
                Les cartes de ce groupe seront attribuées au lancement : la liste s&apos;affichera alors.
              </p>
            ) : nbSansCarte > 0 ? (
              <p className="font-bold" style={{ color: QCM_AMBER, fontSize: 'clamp(13px, 1.3vw, 17px)' }}>
                {nbSansCarte} élève{nbSansCarte > 1 ? 's' : ''} sans carte
              </p>
            ) : null}
          </div>
          <div className="shrink-0">{panneau}</div>
        </div>

        {!groupeEnCharge && lignesGroupe.length === 0 ? (
          <p className="mt-8 text-center font-semibold text-white/50" style={{ fontSize: 'clamp(14px, 1.5vw, 20px)' }}>
            Liste du groupe indisponible : vous pouvez lancer le quizz, les cartes
            scannées resteront reconnues.
          </p>
        ) : null}

        <ul
          className="mt-5 flex-1 min-h-0 overflow-y-auto grid gap-2 content-start"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}
          data-testid="liste-groupe"
        >
          {lignesGroupe.map(({ code, eleve }) => (
            <li key={eleve.id} className="flex items-center gap-3 px-4 py-2 rounded-xl" style={CADRE_LIGNE}>
              <span className="flex-1 min-w-0 break-words font-semibold leading-snug" style={{ fontSize: 'clamp(14px, 1.5vw, 21px)' }}>
                <span className="uppercase">{eleve.nom}</span> {eleve.prenom}
              </span>
              {code === null ? <SansCarte /> : <NumeroCarte code={code} />}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Ni classe ni groupe : le panneau seul, centré.
  if (!avecClasse) {
    return (
      <div className="h-screen w-full flex items-center justify-center p-8 overflow-hidden text-white" style={{ background: QCM_NAVY_BG }}>
        <div className="proj-fade-up text-center space-y-6">
          {titre ? (
            <p className="font-bold text-[13px] uppercase text-white/40" style={{ letterSpacing: '.16em', fontFamily: FONT_TITRE }}>
              {titre}
            </p>
          ) : null}
          {panneau}
        </div>
      </div>
    );
  }

  return (
    <div
      // pt-16 : le bouton « ← Retour » de la projection est fixé en haut à
      // gauche ; sans cette marge, le titre de la séance passait dessous.
      className="h-screen w-full flex flex-col overflow-hidden text-white px-8 pt-16 pb-6"
      style={{ background: QCM_NAVY_BG }}
    >
      <div className="proj-fade-up flex items-start gap-8 shrink-0">
        <div className="flex-1 min-w-0 space-y-1">
          {titre ? (
            <p
              className="font-bold text-[13px] uppercase text-white/40"
              style={{ letterSpacing: '.16em', fontFamily: FONT_TITRE }}
            >
              {titre}
            </p>
          ) : null}
          {/* Consigne ÉLÈVE d'abord, lisible de loin : c'est cet écran qui
              fait foi pour la distribution des cartes. */}
          <p
            className="font-black leading-[1.15]"
            style={{ fontSize: 'clamp(24px, 3.2vw, 44px)', fontFamily: FONT_TITRE }}
          >
            Retrouve ton numéro de carte
          </p>
          <p className="font-semibold text-white/70 leading-snug" style={{ fontSize: 'clamp(15px, 1.6vw, 22px)' }}>
            Prends la carte qui porte ce numéro, puis attends le lancement.
          </p>
          {sansCarte > 0 ? (
            <p className="font-bold" style={{ color: QCM_AMBER, fontSize: 'clamp(13px, 1.3vw, 17px)' }}>
              {sansCarte} élève{sansCarte > 1 ? 's' : ''} sans carte
            </p>
          ) : null}
          {postEnErreur || deleteEnErreur ? (
            <p className="font-bold text-rose-300" style={{ fontSize: 'clamp(13px, 1.3vw, 17px)' }}>
              La dernière modification n'a pas été enregistrée. La liste ci-dessous
              reste celle du serveur.
            </p>
          ) : null}
        </div>
        <div className="shrink-0">{panneau}</div>
      </div>

      {!rosterEnCharge && eleves.length === 0 ? (
        <p
          className="mt-8 text-center font-semibold text-white/50"
          style={{ fontSize: 'clamp(14px, 1.5vw, 20px)' }}
        >
          Liste de la classe indisponible : vous pouvez lancer le quizz, les
          cartes scannées resteront reconnues.
        </p>
      ) : null}

      {/* Colonnes 340px min et noms JAMAIS tronqués (revue prof 2026-09-01) :
          l'élève doit lire son nom entier depuis sa place, un nom long passe
          sur deux lignes plutôt qu'en « FONTAINE GRONDIN… ». */}
      <ul
        className="mt-5 flex-1 min-h-0 overflow-y-auto grid gap-2 content-start"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}
      >
        {eleves.map((eleve) => {
          const assignation = carteDeLEleve.get(eleve.id);
          const enEdition = eleveEnEdition === eleve.id;
          const nomComplet = `${eleve.nom} ${eleve.prenom}`;

          return (
            <li
              key={eleve.id}
              className="flex items-center gap-3 px-4 py-2 rounded-xl"
              style={CADRE_LIGNE}
            >
              <span
                className="flex-1 min-w-0 break-words font-semibold leading-snug"
                style={{ fontSize: 'clamp(14px, 1.5vw, 21px)' }}
              >
                <span className="uppercase">{eleve.nom}</span> {eleve.prenom}
              </span>

              {enEdition ? (
                <>
                  <select
                    autoFocus
                    value=""
                    disabled={enCours}
                    onChange={choisir(eleve)}
                    aria-label={`Carte de ${nomComplet}`}
                    className="px-2 py-1 rounded-lg bg-white/95 text-gray-800 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-white/60"
                  >
                    <option value="">Choisir une carte…</option>
                    {assignation ? <option value="retirer">Retirer la carte</option> : null}
                    {cartesLibres.map((carte) => (
                      <option key={carte.id} value={carte.id}>
                        Carte {carte.code_aruco}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setEleveEnEdition(null)}
                    aria-label={`Annuler le changement de carte de ${nomComplet}`}
                    className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition"
                  >
                    <X size={16} />
                  </button>
                </>
              ) : (
                <>
                  {assignation ? <NumeroCarte code={assignation.code_aruco} /> : <SansCarte />}
                  {/* Verrouillé pendant une écriture : une seconde
                      réassignation partirait sur une liste déjà périmée. */}
                  <button
                    type="button"
                    disabled={enCours}
                    onClick={() => setEleveEnEdition(eleve.id)}
                    aria-label={`Changer la carte de ${nomComplet}`}
                    className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition disabled:opacity-30"
                  >
                    <Pencil size={16} />
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default ProjectionDistribution;
